import { useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import type { TagCount } from "../../../api/generated";

interface TagInputProps {
  /** Available tags from the org for autocomplete suggestions */
  suggestions: TagCount[];
  /** Tags already on this conversation (to exclude from suggestions) */
  existingTags: string[];
  /** Called when user adds a tag */
  onAdd: (tag: string) => void;
  /** Called when user wants to close the input */
  onClose: () => void;
}

export function TagInput({
  suggestions,
  existingTags,
  onAdd,
  onClose,
}: TagInputProps) {
  const [inputValue, setInputValue] = useState("");

  const existingSet = new Set(existingTags);
  const options = suggestions
    .filter((t) => !existingSet.has(t.name))
    .map((t) => t.name);

  const handleSubmit = (value: string) => {
    const normalized = value.trim().toLowerCase();
    if (normalized && !existingSet.has(normalized)) {
      onAdd(normalized);
    }
    setInputValue("");
  };

  return (
    <Autocomplete
      freeSolo
      size="small"
      options={options}
      inputValue={inputValue}
      onInputChange={(_, value) => setInputValue(value)}
      onChange={(_, value) => {
        if (typeof value === "string" && value) {
          handleSubmit(value);
        }
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" && inputValue) {
          e.preventDefault();
          handleSubmit(inputValue);
        }
        if (e.key === "Escape") {
          onClose();
        }
        if (e.key === "Backspace" && !inputValue) {
          onClose();
        }
      }}
      onBlur={onClose}
      openOnFocus
      autoHighlight
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Add tag..."
          variant="standard"
          autoFocus
          sx={{
            minWidth: 100,
            "& .MuiInput-input": { fontSize: "0.75rem" },
          }}
        />
      )}
      sx={{ width: 140 }}
    />
  );
}
